import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { useProfileStore } from '@/store/profile.store';
import { ProfileConflictModal } from '@/components/profile/ProfileConflictModal';
import { profileService } from '@/services/profile.service';
import { mergeProfiles } from '@/services/profileMerge';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/components/common/Toast';

export interface MainLayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
}

export function MainLayout({ children, showSidebar = true }: MainLayoutProps) {
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const toast = useToast();
  const conflict = useProfileStore((s) => s.conflict);
  const setConflict = useProfileStore((s) => s.setConflict);
  const setProfile = useProfileStore((s) => s.setProfile);
  const setLocalMode = useProfileStore((s) => s.setLocalMode);
  const [resolving, setResolving] = useState(false);

  // Refresh user data after successful Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      refreshUser().then(() => {
        toast.success('Welcome to Pro! Your account has been upgraded.');
      });
      // Remove the checkout param from the URL without triggering a navigation
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      window.history.replaceState(null, '', url.toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUseCloud = useCallback(async () => {
    if (!conflict) return;
    setResolving(true);
    try {
      setProfile(conflict.serverProfile);
      setLocalMode(false);
      setConflict(null);
    } finally {
      setResolving(false);
    }
  }, [conflict, setProfile, setLocalMode, setConflict]);

  const handleUseLocal = useCallback(async () => {
    if (!conflict) return;
    setResolving(true);
    try {
      // Push local profile to server via V2 API
      await profileService.patchProfile(conflict.localProfile as any);
      setProfile(conflict.localProfile);
      setLocalMode(false);
      setConflict(null);
    } catch (err) {
      console.error('[MainLayout] Failed to push local profile:', err);
    } finally {
      setResolving(false);
    }
  }, [conflict, setProfile, setLocalMode, setConflict]);

  const handleMerge = useCallback(async () => {
    if (!conflict) return;
    setResolving(true);
    try {
      const merged = mergeProfiles(conflict.localProfile, conflict.serverProfile);
      await profileService.patchProfile(merged as any);
      setProfile(merged);
      setLocalMode(false);
      setConflict(null);
    } catch (err) {
      console.error('[MainLayout] Failed to merge profiles:', err);
    } finally {
      setResolving(false);
    }
  }, [conflict, setProfile, setLocalMode, setConflict]);

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <Header />
      <div className="flex">
        {showSidebar && (
          <div className="hidden md:block">
            <Sidebar />
          </div>
        )}
        <main className="flex-1 px-4 sm:px-6 pt-2 pb-6 main-content-mobile-safe">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
      {showSidebar && <BottomNav />}

      {/* Profile conflict modal (shown after login when both local + server profiles exist) */}
      {conflict && (
        <ProfileConflictModal
          localProfile={conflict.localProfile}
          serverProfile={conflict.serverProfile}
          onUseCloud={handleUseCloud}
          onUseLocal={handleUseLocal}
          onMerge={handleMerge}
          isLoading={resolving}
        />
      )}
    </div>
  );
}
